import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { unwrapResult } from '@reduxjs/toolkit';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { fetchRegisterCodeResend } from 'api/authApi';
import Button from 'components/ui/Button';
import Link from 'components/ui/Link';
import { login } from 'store/authSlice';

const useStyles = makeStyles()((theme) => ({
  loginInput: {
    ...theme.typography.customInput,
  },
}));

const LoginForm = ({ ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const from = location.state?.from || '/';

  const loginInitialValues = {
    email: '',
    password: '',
    submit: null,
  };
  const loginValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Must be a valid email')
      .max(255)
      .required('Email is required'),
    password: Yup.string().max(255).required('Password is required'),
  });

  const handleLoginSubmit = async ({ email, password }, { setErrors }) => {
    try {
      unwrapResult(await dispatch(login({ email, password })));
      await navigate(from, { replace: true }); // Redirect to previous page
    } catch (err) {
      if (err.code === 'NEW_PASSWORD_REQUIRED') {
        navigate({
          pathname: '/change-password',
          search: createSearchParams({ email, temp: password }).toString(),
        });
      } else if (err.code === 'UserNotConfirmedException') {
        setErrors({
          submit:
            'Your email has not yet been verified. Please click on the verification link we sent to your email address and try again.',
        });
        fetchRegisterCodeResend({ email });
      } else {
        setErrors({ submit: err.message });
      }
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Grid
          item
          xs={12}
          container
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="subtitle1">
            Sign in with Email address
          </Typography>
        </Grid>
      </Grid>

      <Formik
        initialValues={loginInitialValues}
        validationSchema={loginValidationSchema}
        onSubmit={handleLoginSubmit}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit} {...props}>
            <FormControl
              fullWidth
              error={Boolean(touched.email && errors.email)}
              className={classes.loginInput}
            >
              <InputLabel htmlFor="outlined-adornment-email-login">
                Email Address
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-email-login"
                type="email"
                value={values.email}
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                label="Email Address"
                inputProps={{
                  classes: {
                    notchedOutline: classes.notchedOutline,
                  },
                }}
              />
              {touched.email && errors.email && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-email-login"
                >
                  {errors.email}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl
              fullWidth
              error={Boolean(touched.password && errors.password)}
              className={classes.loginInput}
            >
              <InputLabel htmlFor="outlined-adornment-password-login">
                Password
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-password-login"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                inputProps={{
                  classes: {
                    notchedOutline: classes.notchedOutline,
                  },
                }}
              />
              {touched.password && errors.password && (
                <FormHelperText
                  error
                  id="standard-weight-helper-text-password-login"
                >
                  {errors.password}
                </FormHelperText>
              )}
            </FormControl>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={1}
            >
              <Typography
                variant="subtitle1"
                component={Link}
                href="/forgot-password"
                color="secondary"
                sx={{ textDecoration: 'none' }}
              >
                Forgot Password?
              </Typography>
            </Stack>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}

            <Button
              disableElevation
              isLoading={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              color="secondary"
              sx={{ mt: 2 }}
            >
              Sign in
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};
LoginForm.propTypes = {
  loginIndex: PropTypes.number,
};

export default LoginForm;
